/**
 * Require-IndexedDB startup capability gate — Bombproof storage foundation,
 * step 6 (board 12-imgstore; approval-gate §7).
 *
 * IndexedDB is a documented hard requirement (CLAUDE.md "Requires: WebGL 2.0,
 * IndexedDB, ES Modules") and, under the ratified single-tier design, the SOLE
 * document store. A browser without it gets a clear "unsupported browser"
 * message at startup — never a degraded document store (a silent localStorage
 * degradation would resurrect the split-brain/stale-shadow data-loss class
 * this foundation removes).
 *
 * The probe matches the existing capability check at
 * `storageAdapters.ts` `IndexedDBAdapter.isAvailable()`.
 */

/** True iff the browser exposes IndexedDB (the app's document store). */
export function isIndexedDbSupported(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Render the plain-HTML unsupported-browser message into `root` in place of
 * the app. Deliberately dependency-free: no React, no storage, no state — it
 * must work on exactly the browsers the gate excludes.
 */
export function renderUnsupportedBrowserMessage(root: HTMLElement): void {
  root.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#1a1a1e;color:#e8e8ea;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;text-align:center;padding:24px;">
      <div style="max-width:520px;">
        <h1 style="font-size:22px;margin:0 0 12px;">This browser isn&rsquo;t supported</h1>
        <p style="font-size:15px;line-height:1.5;margin:0 0 8px;color:#b8b8bc;">
          Stagecraft needs IndexedDB to store your projects safely, and this
          browser doesn&rsquo;t provide it (or has it disabled &mdash; for
          example in some private-browsing modes).
        </p>
        <p style="font-size:15px;line-height:1.5;margin:0;color:#b8b8bc;">
          Please use a current version of Chrome, Edge, Firefox, or Safari
          with private browsing off.
        </p>
      </div>
    </div>`;
}
